import React, { useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'
import { Typography } from '@mui/material'
import clsx from 'clsx'
import dayjs from 'dayjs'

import Loading from '../Loading'
import Button from '../MUI/Button'
import { DataProp } from '../Table'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table'
import { getDefaultMRTOptions } from 'src/utils/getDefaultMRTOptions'
import PrimSecTextCombo from '../common/PrimSecTextCombo'

const GET_EXTERNAL_USAGE_LOGS = gql`
  query GetExternalAircraftUsageLogs(
    $orgSlug: String!
    $externalAircraftId: String!
  ) {
    externalAircraftUsageLogs(
      orgSlug: $orgSlug
      filter: { externalAircraftId: $externalAircraftId }
      pagination: { first: 50 }
    ) {
      edges {
        node {
          id
          usageAsOf
          createdAt
          ExternalComponentUsageLog {
            id
            hours
            cycles
            component {
              id
              name
            }
          }
        }
      }
    }
  }
`

type RowData = DataProp & {
  id: string
  usageAsOf: string
  createdAt: string
  ExternalComponentUsageLog: Array<{
    id: string
    hours: number | null
    cycles: number | null
    component: {
      id: string
      name: string
    }
  }>
}

type HistoricalUsageLogTableProps = {
  orgSlug: string
  externalAircraftId: string
  onEdit?: (usageLogId: string) => void
  onApply?: (usageLogId: string) => void
}

const CellData: React.FC<{
  columnKey: string
  data: RowData
  componentName?: string
}> = ({ data, componentName, columnKey }) => {
  // Handle date column separately - it doesn't need component matching
  if (columnKey === 'date') {
    return (
      <Typography variant="body2" className="min-w-[60px]">
        {dayjs(data.usageAsOf).format('MMM DD, YYYY')}
      </Typography>
    )
  }

  // For other columns, we need to find the component log
  const componentLog = data.ExternalComponentUsageLog.find(
    (log) => log.component.name === componentName
  )
  if (!componentLog) return null

  switch (columnKey) {
    case 'hours-landings':
      return (
        <div className="flex items-center gap-2">
          <PrimSecTextCombo
            primaryText={componentLog.hours || 0}
            secondaryText="Hours"
            variant="inverted"
          />
          <PrimSecTextCombo
            primaryText={componentLog.cycles || 0}
            secondaryText="Landings"
            variant="inverted"
          />
        </div>
      )
    case 'hours-cycles':
      return (
        <div className="flex items-center gap-2">
          <PrimSecTextCombo
            primaryText={componentLog.hours || 0}
            secondaryText="Hours"
            variant="inverted"
          />
          <PrimSecTextCombo
            primaryText={componentLog.cycles || 0}
            secondaryText="Cycles"
            variant="inverted"
          />
        </div>
      )
    default:
      return null
  }
}

const HistoricalUsageLogTable: React.FC<HistoricalUsageLogTableProps> = ({
  orgSlug,
  externalAircraftId,
  onEdit,
  onApply,
}) => {
  const { data, loading } = useQuery(GET_EXTERNAL_USAGE_LOGS, {
    variables: {
      orgSlug,
      externalAircraftId,
    },
  })

  // Memoize the data to prevent infinite re-renders
  const tableData = useMemo(() => {
    const mappedData =
      data?.externalAircraftUsageLogs?.edges?.map((edge: any) => edge.node) ||
      []
    return mappedData
  }, [data?.externalAircraftUsageLogs?.edges])

  const defaultTableOptions = getDefaultMRTOptions<any>()

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: 'date',
        header: 'Date',
        size: 150,
        Cell: ({ row }) => <CellData columnKey="date" data={row.original} />,
      },
      {
        id: 'airframe',
        header: `Airframe`,
        size: 150,
        Cell: ({ row }) => (
          <CellData
            columnKey="hours-landings"
            data={row.original}
            componentName={'Airframe'}
          />
        ),
      },
      {
        id: 'engine1',
        header: `Engine 1`,
        size: 150,
        Cell: ({ row }) => (
          <CellData
            columnKey="hours-cycles"
            data={row.original}
            componentName={'Engine 1'}
          />
        ),
      },
      {
        id: 'engine2',
        header: `Engine 2`,
        size: 150,
        Cell: ({ row }) => (
          <CellData
            columnKey="hours-cycles"
            data={row.original}
            componentName={'Engine 2'}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        Cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              disabledTooltip="Upcoming feature: Edit this time"
              tooltip="Edit this time"
              disabled
              variant="outlined"
              size="small"
            >
              EDIT
            </Button>

            <Button
              tooltip="Select this time"
              variant="outlined"
              size="small"
              onClick={() => {
                onApply?.(row.original.id)
              }}
            >
              APPLY
            </Button>
          </div>
        ),
      },
    ],
    [onApply]
  )

  const table = useMaterialReactTable({
    ...defaultTableOptions,
    columns,
    data: tableData,
    enableTopToolbar: false,
    enableColumnFilters: false,
    enablePagination: true,
    muiTablePaperProps: {
      elevation: 0,
      className: ' border border-solid border-gray-200 w-full',
    },
    muiTableContainerProps: {
      className: 'overflow-auto',
    },
    // Add this to control column sizing
    defaultColumn: {
      size: 'auto', // default column width
    },
    state: {
      isLoading: loading,
      columnPinning: {
        right: ['actions'],
        left: ['date'],
      },
    },
  })

  return (
    <>
      <div
        className={clsx({
          'flex h-52 items-center justify-center': true,
          hidden: !loading,
        })}
      >
        <Loading />
      </div>
      <div
        className={clsx({
          'flex flex-col gap-2 px-2': true,
          hidden: loading,
        })}
      >
        <Typography variant="h6">Historical Usage Log</Typography>
        <MaterialReactTable table={table} />
      </div>
    </>
  )
}

export default HistoricalUsageLogTable
